import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkle, LockSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import PasswordInput from "./components/PasswordInput";

/**
 * Sprint H · Batch 6 — Pantalla "Establecer nueva contraseña".
 *
 * Recibe el `token` por query string desde el email Resend. Valida
 * primero contra el backend (GET /auth/reset-password/validate) y muestra
 * estado claro (expired / used / invalid / valid). En éxito redirige al
 * login adecuado según el `kind` que devuelve el endpoint.
 */
const REASON_MAP = {
  expired:  "El enlace ha caducado. Solicita uno nuevo.",
  used:     "Este enlace ya ha sido utilizado.",
  not_found:"Enlace no válido o ya consumido.",
  missing:  "Falta el token del enlace.",
  corrupt:  "Enlace corrupto.",
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [validation, setValidation] = useState(null);  // { valid, kind, reason }
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidation({ valid: false, reason: "missing" });
      return;
    }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setValidation(data))
      .catch(() => setValidation({ valid: false, reason: "not_found" }));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (pwd.length < 8) {
      setErr("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwd !== pwd2) {
      setErr("Las contraseñas no coinciden.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "No se pudo actualizar la contraseña.");
      setBusy(false);
    }
  };

  if (validation == null) {
    return (
      <div className="min-h-screen ag-light-surface flex flex-col">
        <GlobalHeader variant="auto" testId="reset-header-loading" showRegisterCta={false} />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center text-sm text-gray-500" data-testid="reset-loading">Validando enlace…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ag-light-surface flex flex-col" data-testid="reset-password-page">
      <GlobalHeader variant="auto" testId="reset-header" showRegisterCta={false} />
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-md shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-6">
          <Sparkle size={24} weight="duotone" className="text-[var(--ag-gold)]" />
          <span className="font-display font-black tracking-tighter text-xl text-[#1A1A1A]">AutoGlow</span>
        </div>

        {!validation.valid && (
          <div data-testid="reset-invalid">
            <div className="flex items-center gap-2 text-red-600 mb-3">
              <WarningCircle size={24} weight="fill" />
              <span className="font-display font-bold">Enlace no válido</span>
            </div>
            <p className="text-sm text-gray-700">{REASON_MAP[validation.reason] || "Enlace no válido."}</p>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm text-[var(--ag-gold)] hover:underline" data-testid="reset-request-new">
              Solicitar un nuevo enlace
            </Link>
          </div>
        )}

        {validation.valid && !done && (
          <>
            <h1 className="text-xl font-bold mb-1 text-[#1A1A1A]">Nueva contraseña</h1>
            <p className="text-sm text-gray-600 mb-6" data-testid="reset-account-notice">
              Se restablecerá la contraseña de tu cuenta. Esta acción cerrará todas las
              sesiones activas del usuario en todos los dispositivos.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="new-pwd" className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
                <PasswordInput
                  id="new-pwd"
                  autoComplete="new-password"
                  placeholder="Mínimo 8 caracteres"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="rounded-sm mt-1"
                  testId="reset-new-pwd"
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-pwd2" className="text-xs uppercase tracking-wider">Repite la contraseña</Label>
                <PasswordInput
                  id="new-pwd2"
                  autoComplete="new-password"
                  placeholder="Repite la nueva contraseña"
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="rounded-sm mt-1"
                  testId="reset-new-pwd2"
                  required
                />
              </div>
              {err && <div className="text-xs text-red-600" data-testid="reset-error">{err}</div>}
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-11"
                data-testid="reset-submit"
              >
                <LockSimple size={16} weight="fill" className="mr-1.5" />
                {busy ? "Guardando…" : "Establecer contraseña"}
              </Button>
            </form>
          </>
        )}

        {done && (
          <div data-testid="reset-success">
            <div className="flex items-center gap-2 text-emerald-600 mb-3">
              <CheckCircle size={28} weight="fill" />
              <span className="font-display font-bold text-lg">Contraseña actualizada</span>
            </div>
            <p className="text-sm text-gray-600">
              Ya puedes iniciar sesión con la nueva contraseña.
            </p>
            <Button
              onClick={() => navigate(validation.kind === "customer" ? "/cliente/login" : "/login")}
              className="w-full mt-4 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-11"
              data-testid="reset-goto-login"
            >
              Ir a inicio de sesión
            </Button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
